import { useCallback, useContext, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { FormatContent, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSearchBox } from 'src/core/POS/PosSearchBox';
import { SearchResultPickerDialog } from 'src/dialogs/SearchResultPickerDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getMarketplacePaymentDetailsModalConfigWithDeepLink } from 'src/utils/marketplacePaymentUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  MarketplacePaymentClient,
  MarketplaceSalePayment,
} from 'src/WebApiController';

import { PaymentBasicDisplay } from './PaymentBasicDisplay';

export const PaymentsSearchBox = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { setModal } = useContext(ModalContext);
  const { closeDialog, launchDialog, dialogProps } = useBasicDialog();
  const [searchId, setSearchId] = useState<string>();
  const { showErrorDialog } = useClientLogContext();
  const searchPlaceholder = useContent(ContentId.SearchPaymentId);

  const [searchResults, setSearchResults] =
    useState<MarketplaceSalePayment[]>();

  const onLookupById = useCallback(
    async (input: string) => {
      setSearchId(input);
      const marketplacePayments = await tryInvokeApi(
        () =>
          new MarketplacePaymentClient(
            activeAccountWebClientConfig
          ).getMarketplacePaymentsByExternalPaymentId(input),
        (error) => {
          showErrorDialog(
            'MarketplacePaymentClient.getMarketplacePaymentsByExternalPaymentId',
            error,
            { trackErrorData: { externalPaymentId: input } }
          );
        }
      );

      setSearchResults(marketplacePayments || []);

      if (!marketplacePayments || marketplacePayments.length === 0) {
        launchDialog();
        return false;
      }

      if (marketplacePayments.length == 1) {
        setModal(
          getMarketplacePaymentDetailsModalConfigWithDeepLink(
            marketplacePayments[0].id
          )
        );
        return true;
      } else if (marketplacePayments.length > 1) {
        launchDialog();
      }
      return false;
    },
    [activeAccountWebClientConfig, launchDialog, setModal, showErrorDialog]
  );

  const onResultItemPicked = useCallback(
    (msp: MarketplaceSalePayment) => {
      setModal(getMarketplacePaymentDetailsModalConfigWithDeepLink(msp.id));
    },
    [setModal]
  );

  return (
    <>
      <PosSearchBox
        placeholder={searchPlaceholder}
        onEnterKeyPressed={(curValue) => {
          onLookupById(curValue);
        }}
      />
      <SearchResultPickerDialog
        {...dialogProps}
        header={
          <FormatContent
            id={
              searchResults?.length
                ? FormatContentId.SearchResultPick
                : FormatContentId.CouldNotFindPaymentId
            }
            params={[searchId || '', `${searchResults?.length || 0}`]}
          />
        }
        resultItems={searchResults || []}
        renderItem={(marketplacePayment) => (
          <PaymentBasicDisplay
            marketplaceSalePaymentData={marketplacePayment}
          />
        )}
        onResultItemPicked={onResultItemPicked}
        onCancel={closeDialog}
        showItemBorder
      />
    </>
  );
};
